import { Heading } from "@solomon-labs/design/components/ui";
import React from "react";

import { Badge } from "../../../components/Badge/Badge";
import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";

import {
    type NextDistribution,
    NextDistributionMeter
} from "./NextDistributionMeter";

/** One chip in the active-header tags row, pre-resolved (label + tone). */
export interface HeaderTag {
    /** Chip label text. */
    label: string;
    /** Chip tone. `septenary` = target, `default` = region, `sand` = funding. */
    tone?: "default" | "sand" | "septenary";
}

export interface ProgramActiveHeaderProps {
    /** The active-program title. */
    name: string;
    /** Status-badge label (e.g. "Active"). */
    statusLabel: string;
    /** Status-badge dot state. Defaults to `success` (Active). */
    statusState?: "success" | "pending" | "disabled" | "error";
    /** Text-color utility for the status label (e.g. `text-success-base`). */
    statusLabelClassName?: string;
    /** Pre-resolved tag chips — target / region(s) / funding source(s). */
    tags: HeaderTag[];
    /** Next-distribution cycle — drives the live countdown meter on the right.
     *  `null` for programs with no active distribution cycle. */
    nextDistribution: NextDistribution | null;
}

/**
 * ProgramActiveHeader — **View** (props-only). The read-only active-program
 * title block at the top of the program detail view: a large `Heading` title
 * + a `StatusBadge` on the title row, a `Tags` row of pre-resolved target /
 * region / funding chips, and a `NextDistributionMeter` (the live countdown)
 * on the right.
 *
 * The active branch of the app's mode-aware `ProgramHeader`, re-composed here
 * props-only for the Viewing-Program read view — no draft `ProgramNameField`,
 * no `Program` entity, no `programHelpers`. The Container (app-side
 * `ProgramHeader`) still owns the create/view fork + the domain →
 * status/tone/tag mapping and forwards the resolved values; the sim seeds
 * them from fixtures.
 *
 * Header layout is **container-responsive** — the `px-4` wrapper is a named
 * container (`@container/hdr`) and the layout switches on the header's own
 * width (mirrors the Qualified Users Table's `@container/qutable` pattern), so
 * it reflows correctly inside a narrow detail pane, not just at page-width:
 *   - **Narrow (< `@xl/hdr` = 576px):** STACK vertically — title, then the
 *     status below it, the tags row, then the meter on its own full-width line
 *     below. The title gets the full pane width, and the status drops under it
 *     so it never overflows the narrow pane's right edge.
 *   - **Wide (≥ `@xl/hdr`):** the ROW — `shrink-0` left block (title one line
 *     via `whitespace-nowrap` + status, then a `mt-3` tags row) on the left,
 *     the flex-shrinkable meter slot (`flex-1 min-w-[228px] max-w-[236px]` —
 *     sized to the compact meter's top-row floor of ~223px) on the right.
 *
 * Breakpoint rationale: a fitting row needs title (~264px nowrap) + gap-8 +
 * meter (~228px) + the status badge ≈ 560–600px of header width. `@xl` (576px)
 * is the first container step in that band — stack below it, row at/above.
 */
export function ProgramActiveHeader({
    name,
    statusLabel,
    statusState = "success",
    statusLabelClassName = "text-success-base",
    tags,
    nextDistribution
}: ProgramActiveHeaderProps): React.ReactElement {
    return (
        <section className="py-4">
            {/* Named container (`@container/hdr`) — the flex row BELOW it is a
                descendant, so its `@xl/hdr:*` variants respond to THIS wrapper's
                own width, not the viewport (a container query never applies to
                the container element itself — mirrors `@container/qutable`, which
                marks the Table wrapper and gates the row/cell children). */}
            <div className="@container/hdr px-4">
                {/* Layout switch: STACK (flex-col) < @xl/hdr = 576px so the
                    title gets the full pane width and the meter drops to its own
                    line below; ROW ≥ @xl/hdr — left block + meter side-by-side. */}
                <div className="flex flex-col gap-4 @xl/hdr:flex-row @xl/hdr:items-start @xl/hdr:justify-between @xl/hdr:gap-8">
                    {/* Left block shrinks last (`shrink-0`) in ROW mode so the
                        title + tags win space over the meter, which flex-shrinks
                        first. */}
                    <div className="@xl/hdr:shrink-0">
                        {/* Title + status: STACK (status drops below the title)
                            < @xl/hdr so the status never overflows the narrow
                            pane's right edge; inline ROW ≥ @xl/hdr. */}
                        <div className="flex flex-col items-start gap-2 @xl/hdr:flex-row @xl/hdr:items-center @xl/hdr:gap-8">
                            <Heading
                                title={name}
                                titleVariant="h2"
                                tone="base"
                                // whitespace-nowrap keeps the title on one line —
                                // the meter yields space to it, not the reverse.
                                titleClassName="text-3xl whitespace-nowrap"
                            />
                            <StatusBadge
                                state={statusState}
                                label={statusLabel}
                                labelClassName={statusLabelClassName}
                            />
                        </div>
                        {tags.length > 0 && (
                            // Tag group — a flex-wrap row of chips (the app's
                            // `Tags` GC, inlined here; it's the only consumer).
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                {tags.map((tag) => (
                                    <Badge key={tag.label} tone={tag.tone}>
                                        {tag.label}
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                    {nextDistribution && (
                        // ROW mode (≥ @xl/hdr): compact meter (no bottom values) →
                        // size the slot to its top row: "Next Distribution" label
                        // (~102px) + gap-4 (16px) + the `Xd HH:MM:SS` countdown
                        // (~105px at max digits) ≈ 223px natural floor.
                        // min-w-[228px] holds that with a few px of headroom so the
                        // label + countdown never collide; the max-width steps up
                        // with the header's own container width so a wide/full-screen
                        // header fills the gap between title and meter instead of
                        // stranding it at the compact ~236px cap. `flex-1` keeps it
                        // yielding space to the title first, then grows toward the
                        // (breakpoint-scaled) max. STACKED mode (< @xl/hdr): none of
                        // these apply — the meter is a full-width line below the
                        // title, no side-by-side, no overlap.
                        // why: measured top-row floor + hand-tuned wide-header caps;
                        //      none sit on the Tailwind max-width scale.
                        <div className="@xl/hdr:flex-1 @xl/hdr:min-w-[228px] @xl/hdr:max-w-[236px] @4xl/hdr:max-w-[300px] @5xl/hdr:max-w-[360px] @6xl/hdr:max-w-[420px]">
                            <NextDistributionMeter data={nextDistribution} />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
